import React, { useContext, useEffect, useState } from 'react';
import { DrawerComponent } from 'components/Drawer';
import { FeatureContext } from 'contexts/FeaturesContext';
import { api } from 'services/api';

type Promotion = {
  name: string;
  valid_until: string;
};

type KoinTier = {
  id: number;
  name: string;
  koin_quantity: number;
  discount_percentage: number;
  sort_order: number;
  max_installments: number;
  unit_price_brl: number;
  full_price_brl: number;
  total_price_brl: number;
  upfront_price_brl: number;
  upfront_discount_pct: number;
  installment_price_brl: number | null;
  installment_discount_pct: number | null;
  promo_max_installments: number | null;
  installment_unit_price_brl: number | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  accessToken: string | null;
  idToken: string | null;
};

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);


const PromoBadge = ({ name }: { name: string }) => (
  <span
    style={{
      background:
        'linear-gradient(to right, #B1D6F6, #56A6EB, #3B2358, #B77CCB, #DFC5E8)',
      color: '#fff',
      fontSize: '11px',
      fontWeight: 600,
      borderRadius: '12px',
      padding: '4px 8px',
      whiteSpace: 'nowrap',
    }}
  >
    {name}
  </span>
);

export const ComprarKoinsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  accessToken,
  idToken,
}) => {
  const { dataAgency } = useContext(FeatureContext);
  const [tiers, setTiers] = useState<KoinTier[]>([]);
  const [promotion, setPromotion] = useState<Promotion | null>(null);
  const [selectedTier, setSelectedTier] = useState<KoinTier | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isParcelarOpen, setIsParcelarOpen] = useState(false);

  const userName = localStorage.getItem('name') ?? '';
  const companyName = dataAgency?.name ?? '';

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    const fetchTiers = async () => {
      try {
        const { data } = await api.get('lya/koin-tiers', {
          headers: { Authorization: `Bearer ${accessToken}`, idToken },
        });
        const fetchedTiers: KoinTier[] = data.data.tiers;
        setTiers(fetchedTiers);
        setPromotion(data.data.active_promo ?? null);
        if (fetchedTiers.length > 0) setSelectedTier(fetchedTiers[0]);
      } catch (e) {
        setError('Não foi possível carregar os pacotes de koins.');
      }
    };
    fetchTiers();
  }, [isOpen, accessToken, idToken]);



  const handleBuyAVista = async (is_upfront?:boolean) => {
    if (!selectedTier) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(
        'lya/buy',
        {
          id_tier: selectedTier.id,
          koin_quantity: selectedTier.koin_quantity,
          amount_brl: selectedTier.total_price_brl,
          is_upfront:is_upfront,
          success_url: `${window.location.origin}/lya/koin/success`,
          cancel_url: window.location.href,
        },
        { headers: { Authorization: `Bearer ${accessToken}`, idToken } },
      );
      if (data.data?.checkout_url) {
        const tab = window.open(data.data.checkout_url, '_blank');
        if (tab) {
          let wentCrossOrigin = false;
          const poll = setInterval(() => {
            try {
              if (tab.closed) {
                clearInterval(poll);
                onClose();
                return;
              }
              // Only act when we've already been to pagar.me and came back
              if (wentCrossOrigin && tab.location.href) {
                tab.close();
                clearInterval(poll);
                onClose();
              }
            } catch {
              // Cross-origin exception = still on pagar.me
              wentCrossOrigin = true;
            }
          }, 500);
        }
      }
    } catch (e) {
      setError('Erro ao iniciar compra. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = () => {
    if (!selectedTier) return;
    const koinQty = selectedTier.koin_quantity.toLocaleString('pt-BR');
    const message = `Olá! Meu nome é ${userName} da ${companyName}. Quero parcelar o pacote de ${koinQty} koins da Lya.`;
    window.open(
      `https://wa.me/5511992359399?text=${encodeURIComponent(message)}`,
      '_blank',
    );
  };

  const hasPromotion =
    !!promotion && new Date(promotion.valid_until) > new Date();

  const parcelarTotal = selectedTier?.installment_price_brl ?? null;
  const canInstall = hasPromotion
    ? (selectedTier?.promo_max_installments ?? 0) > 1
    : (selectedTier?.max_installments ?? 0) > 1;

  return (
    <>
      <DrawerComponent
        open={isOpen}
        size='default'
        titleClose='Comprar koins'
        extra={hasPromotion ? <PromoBadge name={promotion!.name} /> : undefined}
        onCompleteClose={onClose}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            paddingBottom: '24px',
          }}
        >
          <p
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#333',
              margin: '8px 0 14px',
            }}
          >
            Selecione o pacote:
          </p>

          {error && (
            <p
              style={{
                color: '#c0392b',
                fontSize: '13px',
                marginBottom: '12px',
              }}
            >
              {error}
            </p>
          )}

          {/* Tier list */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
            }}
          >
            {tiers.map(tier => {
              const isSelected = selectedTier?.id === tier.id;


              return (
                <div
                  key={tier.id}
                  onClick={() => setSelectedTier(tier)}
                  style={{
                    border: `2px solid ${isSelected ? '#298EE6' : '#e0e0e0'}`,
                    borderRadius: '10px',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    background: '#fff',
                  }}
                >
                  {/* Radio indicator */}
                  <div style={{
                    height: '100%',
                    flex: '1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}>
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: isSelected
                          ? `5px solid #298EE6`
                          : `2px solid #ccc`,
                        flexShrink: 0,

                      }}
                    />

                    {/* Tier name + installment hint */}
                    <div style={{ flex: 1, display: 'flex' }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: '24px',
                          color: '#1a1a1a',
                        }}
                      >
                        {tier.koin_quantity.toLocaleString('pt-BR')}
                      </div>
                      <div
                        style={{
                          color: '#999',
                          fontSize: '16px',
                          marginBottom: "5px",
                          marginLeft: "8px",
                          alignSelf: "end"
                        }}
                      >
                        Koins
                      </div>
                    </div>
                  </div>
                  {/* Price block */}
                  {(() => {
                    const displayPrice = tier.upfront_price_brl
                    const displayDiscount =
                      tier.upfront_discount_pct

                    const installmentCount = hasPromotion
                      ? (tier.promo_max_installments != null && tier.promo_max_installments > 1 ? tier.promo_max_installments : null)
                      : tier.max_installments > 1 ? tier.max_installments : null;
                    const installmentUnitPrice =
                      installmentCount && tier.installment_price_brl
                        ? hasPromotion
                          ? tier.installment_unit_price_brl
                          : tier.installment_price_brl / installmentCount
                        : null;

                    return (
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div
                          style={{
                            color: '#999',
                            fontSize: '16px',
                            textDecoration: 'line-through',
                            marginBottom: '2px',
                          }}
                        >
                          {formatBRL(tier.full_price_brl)}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            justifyContent: 'flex-end',
                          }}
                        >
                          {displayDiscount > 0 && (
                            <span
                              style={{
                                color: '#D93E26',
                                fontSize: '12px',
                                fontWeight: 600,
                              }}
                            >
                              -{displayDiscount}% à vista
                            </span>
                          )}
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: '24px',
                              color: '#1a1a1a',
                            }}
                          >
                            {formatBRL(displayPrice)}
                          </span>
                        </div>
                        {installmentCount && installmentUnitPrice && (
                          <div>
                            <span
                              style={{
                                color: '#999',
                                fontSize: '12px',
                                fontWeight: 600,
                              }}
                            >
                              ou -{tier.installment_discount_pct}% parcelado em {installmentCount}x {formatBRL(installmentUnitPrice)}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}

            {/* Description inside scroll area */}
            <p
              style={{
                color: '#555',
                fontSize: '13px',
                lineHeight: 1.5,
                margin: '16px 0 4px',
                flexShrink: 0,
              }}
            >
              Os pacotes de koins são utilizados quando o saldo do plano acaba e
              são válidos por um ano a partir da data da compra.
            </p>
          </div>

          {/* Footer — only buttons, always visible */}
          <div style={{ flexShrink: 0, paddingTop: '12px' }}>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'end' }}>
              {canInstall && (
                <button
                  onClick={()=>handleBuyAVista(false)}
                  disabled={loading || !selectedTier}
                  style={{
                    padding: '12px 24px',
                    background: loading || !selectedTier ? '#ccc' : 'transparent',
                    color: '#298EE6',
                    border: '1px solid #298EE6',
                    borderRadius: '8px',
                    cursor: loading || !selectedTier ? 'not-allowed' : 'pointer',
                    fontWeight: 700,
                    fontSize: '14px',
                  }}
                >
                  {loading ? 'Aguarde...' : 'Comprar parcelado'}
                </button>
              )}
              <button
                onClick={()=>handleBuyAVista(true)}
                disabled={loading || !selectedTier}
                style={{
                  padding: '12px 24px',
                  background: loading || !selectedTier ? '#ccc' : '#298EE6',
                  color: '#fff',
                  border: '1px solid #298EE6',
                  borderRadius: '8px',
                  cursor: loading || !selectedTier ? 'not-allowed' : 'pointer',
                  fontWeight: 700,
                  fontSize: '14px',
                }}
              >
                {loading ? 'Aguarde...' : 'Comprar à vista'}
              </button>
            </div>
          </div>
        </div>
      </DrawerComponent>

      {/* Parcelar pacote modal */}
      {isParcelarOpen && selectedTier && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '28px 24px 24px',
              width: '90%',
              maxWidth: '600px',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setIsParcelarOpen(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '22px',
                color: '#555',
                lineHeight: 1,
              }}
            >
              ×
            </button>

            <h3
              style={{
                margin: '0 0 20px',
                fontSize: '18px',
                fontWeight: 700,
                color: '#1a1a1a',
              }}
            >
              Parcelar o pacote
            </h3>

            {/* Tier summary card */}
            <div
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '20px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',

                }}
              >
                <span style={{ fontSize: '16px', color: '#808080' }}>
                  Pacote selecionado
                </span>
                {selectedTier.promo_max_installments != null && selectedTier.promo_max_installments > 1 &&
                  selectedTier.installment_unit_price_brl && (
                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#808080' }}>
                      {selectedTier.promo_max_installments}x de{' '}
                      {formatBRL(selectedTier.installment_unit_price_brl)}
                    </span>
                  )}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontWeight: 700, fontSize: '24px' }}>
                  {selectedTier.koin_quantity.toLocaleString('pt-BR')}<span style={{ fontWeight: 500, fontSize: '16px' }}> Koins</span>
                </span>
                {parcelarTotal !== null && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '6px',
                    }}
                  >
                    <span
                      style={{
                        color: '#298EE6',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      -{selectedTier.installment_discount_pct}%
                    </span>
                    <span style={{ fontWeight: 700, fontSize: '24px' }}>
                      {formatBRL(parcelarTotal)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <p
              style={{
                fontSize: '14px',
                color: '#555',
                lineHeight: 1.6,
                marginBottom: '24px',
              }}
            >
              Entre em contato com um de nossos consultores pelo WhatsApp. Eles
              vão te enviar um link de pagamento com as condições do pacote
              selecionado.
            </p>

            <div style={{
              display: 'flex',
              justifyContent: 'end'
            }}>
              <button
                onClick={handleWhatsApp}
                style={{

                  padding: '12px 24px',
                  background: '#298EE6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '16px',
                }}
              >
                Conversar com consultor
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
